import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { z } from 'zod';
import { useLive, useRuntime } from './context';
import { formatMoney } from '../mirror/time';
import { useLocale, useT } from '../messages';

/**
 * **Gift requests — the promises the desk settles** (owner's ruling 1,
 * migration 0051).
 *
 * A guest asked for a gift card on the website and **nobody paid for
 * anything**. That is the whole point: spec §7.4 rule 1 makes a certificate a
 * liability recognised when it is *sold*, so an unpaid ask is not one and never
 * appears in the liability figure above this panel.
 *
 * ## Why this reads the server directly instead of the mirror
 *
 * Everything else on this screen comes from the offline mirror, because a
 * counter has to keep working when the island loses the link. This does not,
 * and the reason is what the list *is*: a queue of people to telephone, which
 * only matters when the desk can telephone them, and which nobody needs to read
 * from a boat. Mirroring it would mean a table, a puller and an outbox verb for
 * a list that is read once a week — and an offline copy of "who is still
 * waiting" would be the one thing on this screen that could be confidently
 * wrong. So it fetches, and it says plainly when it cannot.
 *
 * ## The seam, half closed
 *
 * **"Sell this certificate" prefills the sale** — the service, the length she
 * asked for (migration 0054) and the recipient, or the amount — and scrolls to
 * the issue form, so a card for "the 90-minute facial" is issued at the 90's
 * price without anybody retyping it. What is still two steps is the
 * *fulfilment*: a `gift_request_id` riding through `certificates.issue` so
 * that issuing and marking-sold become one call is written down rather than
 * half-built, and until then "Mark as sold" is the second step and names the
 * card.
 */

/** What "Sell this certificate" hands the issue form. Never a price for a service. */
export interface SellPrefill {
  /** The face value she asked for, when she asked for an amount. */
  amountCents: number | null;
  itemId: string | null;
  /** Which length of `itemId` (0054), when she named one. */
  optionId: string | null;
  /** Who she asked for it to be for — the sheet's recipient search starts there. */
  recipientName: string;
  /** Who asked — the purchaser search starts there (0074: both are clients now). */
  purchaserName?: string | undefined;
}

const GiftRequestRow = z.object({
  gift_request_id: z.string(),
  guest_name: z.string(),
  status: z.enum(['requested', 'fulfilled', 'cancelled']),
  amount_cents: z.number().int().nullable(),
  service_id: z.string().nullable(),
  service_name: z.string().nullable(),
  /** Which length she asked for (0054). Tolerated absent, for a server one release behind. */
  option_id: z.string().nullable().catch(null).default(null),
  option_name: z.string().nullable().catch(null).default(null),
  /** The catalog's number today — the length's, when she named one. */
  service_price_cents: z.number().int().nullable(),
  recipient_name: z.string(),
  message: z.string(),
  created_at: z.string(),
  fulfilled_certificate_id: z.string().nullable(),
  fulfilled_at: z.string().nullable(),
});
type GiftRequestRow = z.infer<typeof GiftRequestRow>;

const GiftRequestList = z.object({ gift_requests: z.array(GiftRequestRow) });

export function GiftRequestsPanel({
  currency,
  onSell,
}: {
  currency: string;
  /** Open the gift card sheet, prefilled from this ask. */
  onSell: (prefill: SellPrefill) => void;
}): ReactNode {
  const runtime = useRuntime();
  const t = useT();
  const locale = useLocale();
  const certificates = useLive(() => runtime.mirror.certificates(), []);

  const [rows, setRows] = useState<GiftRequestRow[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [card, setCard] = useState<Record<string, string>>({});

  const load = useCallback(() => {
    setFailed(false);
    runtime.api
      .request({ method: 'GET', path: '/gift-requests', schema: GiftRequestList })
      .then((body) => {
        setRows(body.gift_requests);
      })
      .catch(() => {
        setFailed(true);
      });
  }, [runtime]);

  useEffect(load, [load]);

  async function act(id: string, path: string, body: Record<string, unknown>): Promise<void> {
    setBusy(id);
    try {
      await runtime.api.request({
        method: 'POST',
        path: `/gift-requests/${encodeURIComponent(id)}/${path}`,
        body,
        schema: z.object({ gift_request_id: z.string() }),
      });
      load();
    } catch {
      setFailed(true);
    } finally {
      setBusy(null);
    }
  }

  if (failed) {
    return (
      <section className="panel">
        <h3>{t('giftRequests.title')}</h3>
        <p className="muted">{t('giftRequests.offline')}</p>
      </section>
    );
  }
  if (rows === null) return null;

  const open = rows.filter((r) => r.status === 'requested');

  return (
    <section className="panel">
      <h3>{t('giftRequests.title')}</h3>
      {/* Said once, at the top, because it is the fact somebody will get wrong:
          none of this is money yet and none of it is in the figure above. */}
      <p className="muted">{t('giftRequests.unpaid')}</p>

      {open.length === 0 ? (
        <p className="muted">{t('giftRequests.none')}</p>
      ) : (
        <ul className="report-list">
          {open.map((row) => (
            <li key={row.gift_request_id}>
              <div>
                <strong>{row.guest_name}</strong>
                <small>
                  {row.amount_cents === null
                    ? t('giftRequests.forService', {
                        // "Custom Deluxe Facial · 90 minutes" when she named a length.
                        name:
                          row.option_name === null
                            ? (row.service_name ?? '')
                            : t('certificates.namedLength', {
                                name: row.service_name ?? '',
                                length: row.option_name,
                              }),
                        price: formatMoney(row.service_price_cents ?? 0, currency, locale),
                      })
                    : formatMoney(row.amount_cents, currency, locale)}
                  {row.recipient_name === ''
                    ? ''
                    : t('giftRequests.forWhom', { name: row.recipient_name })}
                </small>
                {row.message === '' ? null : <small className="muted">{row.message}</small>}
              </div>
              <div className="row-actions">
                <button
                  type="button"
                  className="button"
                  onClick={() =>
                    onSell({
                      amountCents: row.amount_cents,
                      itemId: row.service_id,
                      optionId: row.option_id,
                      recipientName: row.recipient_name,
                      purchaserName: row.guest_name,
                    })
                  }
                >
                  {t('giftRequests.sell')}
                </button>
                <label className="sr-only" htmlFor={`gr-card-${row.gift_request_id}`}>
                  {t('giftRequests.whichCard')}
                </label>
                <select
                  id={`gr-card-${row.gift_request_id}`}
                  value={card[row.gift_request_id] ?? ''}
                  onChange={(e) =>
                    setCard((current) => ({
                      ...current,
                      [row.gift_request_id]: e.target.value,
                    }))
                  }
                >
                  <option value="">{t('giftRequests.whichCard')}</option>
                  {certificates.slice(0, 25).map((c) => (
                    <option key={c.certificate_id} value={c.certificate_id}>
                      {c.code} — {formatMoney(c.value_cents, currency, locale)}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="button button--primary"
                  disabled={
                    busy === row.gift_request_id || (card[row.gift_request_id] ?? '') === ''
                  }
                  onClick={() =>
                    void act(row.gift_request_id, 'fulfil', {
                      certificate_id: card[row.gift_request_id] ?? '',
                      client_intent_id: `gf-${row.gift_request_id}`,
                    })
                  }
                >
                  {t('giftRequests.markSold')}
                </button>
                <button
                  type="button"
                  className="button"
                  disabled={busy === row.gift_request_id}
                  onClick={() =>
                    void act(row.gift_request_id, 'cancel', {
                      client_intent_id: `gc-${row.gift_request_id}`,
                    })
                  }
                >
                  {t('giftRequests.cancel')}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
